<script setup>
// A nota de voz que já foi, tocando na bolha da conversa de teste (A-15): o tocar e pausar, o
// quanto já tocou, e o tempo — o total parado, o que já tocou tocando.
//
// A `url` é a que a bolha tem: no Playground e no modo motor, o `blob:` da gravação, que só existe
// nesta aba (nada vai para disco nem para o S3, D2); no modo canal, o anexo da mensagem, pelo proxy
// do ActiveStorage, na mesma origem da página. A `url` vai para o `<audio>` como veio: um `?t=` no
// fim de um `blob:` quebra a reprodução (o player de mensagens do painel faz isso, e por isso não
// serve aqui).
//
// A duração vem do gravador (`duracaoMs`) quando a bolha a tem: o WebM que o Chrome grava não diz a
// dele até tocar inteiro. Sem ela, vale a do arquivo, quando ele a diz.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  url: { type: String, required: true },
  duracaoMs: { type: Number, default: null },
});

const { t } = useI18n();
const audioRef = ref(null);
const tocando = ref(false);
const tocadoMs = ref(0);
const duracaoDoArquivoMs = ref(null);
const falhou = ref(false);

const totalMs = computed(
  () => props.duracaoMs || duracaoDoArquivoMs.value || 0
);
const progresso = computed(() =>
  totalMs.value ? Math.min(100, (tocadoMs.value / totalMs.value) * 100) : 0
);
const tempoDe = ms => {
  const segundos = Math.floor(ms / 1000);
  return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
};
const tempo = computed(() =>
  tempoDe(tocando.value || tocadoMs.value ? tocadoMs.value : totalMs.value)
);

// O `play` interrompido por um `pause` (AbortError) não é falha: foi quem ouvia que pausou.
function alternar() {
  const audio = audioRef.value;
  if (tocando.value) {
    audio.pause();
    return;
  }
  falhou.value = false;
  Promise.resolve(audio.play()).catch(e => {
    if (e?.name !== 'AbortError') falhou.value = true;
  });
}

function aoCarregar() {
  const segundos = audioRef.value.duration;
  if (Number.isFinite(segundos)) duracaoDoArquivoMs.value = segundos * 1000;
}

function aoAndar() {
  tocadoMs.value = audioRef.value.currentTime * 1000;
}

function aoAcabar() {
  tocando.value = false;
  tocadoMs.value = 0;
}

function aoFalhar() {
  tocando.value = false;
  falhou.value = true;
}
</script>

<template>
  <div class="flex items-center gap-2 min-w-40" data-audio-enviado>
    <button
      type="button"
      class="flex items-center justify-center rounded-full shrink-0 size-7 hover:opacity-80"
      :title="
        tocando
          ? t('CONVERSA_DE_TESTE.AUDIO.PAUSE')
          : t('CONVERSA_DE_TESTE.AUDIO.PLAY')
      "
      :aria-label="
        tocando
          ? t('CONVERSA_DE_TESTE.AUDIO.PAUSE')
          : t('CONVERSA_DE_TESTE.AUDIO.PLAY')
      "
      @click="alternar"
    >
      <i class="size-4" :class="tocando ? 'i-lucide-pause' : 'i-lucide-play'" />
    </button>
    <span v-if="falhou" class="text-xs" role="alert" data-audio-falhou>
      {{ t('CONVERSA_DE_TESTE.AUDIO.PLAY_FAILED') }}
    </span>
    <template v-else>
      <span class="relative flex-1 h-1 overflow-hidden rounded-full">
        <span class="absolute inset-0 bg-current opacity-30" />
        <span
          class="absolute inset-y-0 left-0 bg-current"
          :style="{ width: `${progresso}%` }"
          data-progresso
        />
      </span>
      <span class="text-xs tabular-nums shrink-0 opacity-80" data-tempo>
        {{ tempo }}
      </span>
    </template>
    <audio
      ref="audioRef"
      :src="url"
      preload="metadata"
      class="hidden"
      @play="tocando = true"
      @pause="tocando = false"
      @ended="aoAcabar"
      @timeupdate="aoAndar"
      @loadedmetadata="aoCarregar"
      @error="aoFalhar"
    />
  </div>
</template>
